import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import { format, parseISO } from 'date-fns';
import {
  Shield, CheckCircle, AlertTriangle, XCircle, Clock, FileText, Users,
  ArrowLeft, RefreshCw, Download, Eye, History, Lock, Unlock,
  FileCheck, AlertCircle, Activity, Calendar, ChevronRight,
  Fingerprint, PenLine, UserCheck, ClipboardCheck, Zap, Target,
  TrendingUp, TrendingDown, Minus, Play, Loader2, Wrench, Ban, RotateCcw, Info
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ALCOA+ Principles for display
const ALCOA_PRINCIPLES = [
  { key: 'attributable', name: 'Attributable', icon: UserCheck, color: '#3B82F6', description: 'Data should identify who performed an action and when' },
  { key: 'legible', name: 'Legible', icon: FileText, color: '#8B5CF6', description: 'Data should be readable, permanent, and unambiguous' },
  { key: 'contemporaneous', name: 'Contemporaneous', icon: Clock, color: '#06B6D4', description: 'Data should be recorded at the time of the activity' },
  { key: 'original', name: 'Original', icon: FileCheck, color: '#10B981', description: 'Data should be the first capture or a certified copy' },
  { key: 'accurate', name: 'Accurate', icon: Target, color: '#F59E0B', description: 'Data should be error-free and conform to protocol' },
  { key: 'complete', name: 'Complete', icon: ClipboardCheck, color: '#EC4899', description: 'All data should be present, including repeat analyses' },
  { key: 'consistent', name: 'Consistent', icon: Activity, color: '#6366F1', description: 'Data should follow standardized dating and timing' },
  { key: 'enduring', name: 'Enduring', icon: Shield, color: '#14B8A6', description: 'Data should be recorded on appropriate media' },
  { key: 'available', name: 'Available', icon: Eye, color: '#F97316', description: 'Data should be accessible for review throughout retention period' }
];

// Regulatory Standards
const REGULATORY_STANDARDS = [
  { key: '21_cfr_part_11', name: '21 CFR Part 11', region: 'FDA/USA', description: 'Electronic Records & Signatures', icon: Fingerprint },
  { key: 'ich_gcp_e6', name: 'ICH-GCP E6 R2/R3', region: 'Global', description: 'Good Clinical Practice', icon: ClipboardCheck },
  { key: 'eu_annex_11', name: 'EU Annex 11', region: 'EMA/EU', description: 'Computerized Systems', icon: Shield },
  { key: 'iso_13485', name: 'ISO 13485', region: 'Global', description: 'Medical Devices QMS', icon: FileCheck },
  { key: 'mhra_gxp', name: 'MHRA GxP', region: 'UK', description: 'Data Integrity Guidance', icon: Target },
  { key: 'ema_gxp', name: 'EMA GxP', region: 'EU', description: 'GxP Requirements', icon: Shield },
  { key: 'alcoa_plus', name: 'ALCOA+', region: 'Global', description: 'Data Integrity Principles', icon: CheckCircle }
];

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-50/95 backdrop-blur-sm border border-slate-300 rounded-lg p-3 shadow-xl">
        <p className="text-slate-700 text-sm font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-bold">{entry.value}%</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function ComplianceCenterPage() {
  const { user, getAuthHeaders, isAdmin, selectedStudy: contextStudy } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [bulkAssessing, setBulkAssessing] = useState(false);
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(contextStudy?.study_id || 'all');
  const [dashboardData, setDashboardData] = useState(null);
  const [regulations, setRegulations] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Documents tab state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentDetails, setDocumentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Dismiss action dialog state
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [actionToDismiss, setActionToDismiss] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissingAction, setDismissingAction] = useState(false);
  const [showDismissedActions, setShowDismissedActions] = useState(false);

  // Set selected study from context when it changes
  useEffect(() => {
    if (contextStudy?.study_id) {
      setSelectedStudy(contextStudy.study_id);
    }
  }, [contextStudy]);

  const fetchComplianceData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();

      // Fetch studies
      const studiesRes = await axios.get(`${API_URL}/api/studies`, { headers });
      setStudies(studiesRes.data || []);

      // Fetch compliance dashboard data
      const dashboardUrl = selectedStudy === 'all' 
        ? `${API_URL}/api/compliance/dashboard`
        : `${API_URL}/api/compliance/dashboard?study_id=${selectedStudy}`;
      
      const dashboardRes = await axios.get(dashboardUrl, { headers });
      setDashboardData(dashboardRes.data);

      // Fetch supported regulations
      const regulationsRes = await axios.get(`${API_URL}/api/compliance/regulations`, { headers });
      setRegulations(regulationsRes.data?.regulations || []);

      // Fetch audit logs if admin
      if (isAdmin()) {
        try {
          const logsRes = await axios.get(`${API_URL}/api/admin/audit-logs/paginated?limit=200`, { headers });
          setAuditLogs(logsRes.data?.logs || logsRes.data?.items || []);
        } catch (e) {
          console.log('Could not fetch audit logs');
        }
      }

    } catch (error) {
      console.error('Error fetching compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [selectedStudy, getAuthHeaders, isAdmin]);

  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData]);

  const runBulkAssessment = async () => {
    setBulkAssessing(true);
    try {
      const { headers } = getAuthHeaders();
      const url = selectedStudy === 'all'
        ? `${API_URL}/api/compliance/bulk-assess`
        : `${API_URL}/api/compliance/bulk-assess?study_id=${selectedStudy}`;
      
      const response = await axios.post(url, {}, { headers });
      const data = response.data;
      
      toast.success(`Assessed ${data.total_assessed} documents. Compliant: ${data.compliant}, At Risk: ${data.at_risk}, Non-Compliant: ${data.non_compliant}`);
      
      // Refresh dashboard data
      await fetchComplianceData();
    } catch (error) {
      console.error('Bulk assessment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to run bulk assessment');
    } finally {
      setBulkAssessing(false);
    }
  };

  // Fetch documents with compliance status
  const fetchDocuments = useCallback(async (status = 'all') => {
    setDocumentsLoading(true);
    try {
      const { headers } = getAuthHeaders();
      let url = `${API_URL}/api/compliance/documents?sort_by=compliance_score&sort_order=asc&limit=100`;
      
      if (selectedStudy !== 'all') {
        url += `&study_id=${selectedStudy}`;
      }
      
      if (status !== 'all') {
        url += `&status=${status}`;
      }
      
      const response = await axios.get(url, { headers });
      setDocuments(response.data?.documents || []);
      setStatusCounts(response.data?.status_counts || {});
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  }, [selectedStudy, getAuthHeaders]);

  // Fetch document details for remediation
  const fetchDocumentDetails = async (docId, scrollToActions = false) => {
    setDetailsLoading(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/compliance/document/${docId}/details`, { headers });
      setDocumentDetails(response.data);
      setSelectedDocument(docId);
      
      // Scroll to actions section after data loads
      if (scrollToActions) {
        setTimeout(() => {
          const actionsSection = document.getElementById('required-actions-section');
          if (actionsSection) {
            actionsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching document details:', error);
      toast.error('Failed to load document details');
    } finally {
      setDetailsLoading(false);
    }
  };
  
  // Handler for navigating directly to actions
  const handleNavigateToActions = (e, docId) => {
    e.stopPropagation(); // Prevent card click
    fetchDocumentDetails(docId, true);
  };

  // Handle Fix Issue - Navigate to document
  const handleFixIssue = (action) => {
    if (selectedDocument && documentDetails?.document) {
      // Navigate to document view in dashboard with the doc highlighted
      navigate(`/dashboard?view=all&doc=${selectedDocument}&action=edit`);
    }
  };

  // Handle opening dismiss dialog
  const handleOpenDismissDialog = (action) => {
    setActionToDismiss(action);
    setDismissReason('');
    setDismissDialogOpen(true);
  };

  // Handle dismissing action as "Not Required"
  const handleDismissAction = async (isNotApplicable = true) => {
    if (!actionToDismiss || !dismissReason.trim()) {
      toast.error('Please provide a reason for dismissing this action');
      return;
    }

    setDismissingAction(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/compliance/document/${selectedDocument}/dismiss-action`, {
        requirement_id: actionToDismiss.requirement_id,
        regulation: actionToDismiss.regulation,
        reason: dismissReason,
        is_not_applicable: isNotApplicable
      }, { headers });

      toast.success(`Action marked as ${isNotApplicable ? 'not applicable' : 'dismissed'}`);
      setDismissDialogOpen(false);
      setActionToDismiss(null);
      setDismissReason('');
      
      // Refresh document details
      await fetchDocumentDetails(selectedDocument);
    } catch (error) {
      console.error('Error dismissing action:', error);
      toast.error(error.response?.data?.detail || 'Failed to dismiss action');
    } finally {
      setDismissingAction(false);
    }
  };

  // Handle restoring a dismissed action
  const handleRestoreAction = async (action) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/compliance/document/${selectedDocument}/restore-action?requirement_id=${action.requirement_id}&regulation=${action.regulation}`, {}, { headers });

      toast.success('Action restored');
      
      // Refresh document details
      await fetchDocumentDetails(selectedDocument);
    } catch (error) {
      console.error('Error restoring action:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore action');
    }
  };

  // Load documents when switching to documents tab
  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments(statusFilter);
    }
  }, [activeTab, statusFilter, fetchDocuments]);

  const getOverallScore = () => {
    return dashboardData?.overall_score || 0;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'from-green-500 to-emerald-400';
    if (score >= 60) return 'from-amber-500 to-yellow-400';
    return 'from-red-500 to-rose-400';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 text-red-600 border-red-500/30';
      case 'high': return 'bg-red-400/20 text-red-500 border-red-400/30';
      case 'warning':
      case 'medium': return 'bg-amber-500/20 text-amber-600 border-amber-500/30';
      case 'info':
      case 'low': return 'bg-blue-500/20 text-blue-600 border-blue-500/30';
      default: return 'bg-gray-400/20 text-slate-600 border-gray-400/30';
    }
  };

  const getInspectionReadinessColor = (status) => {
    switch (status) {
      case 'ready': return 'bg-green-500/20 text-green-600';
      case 'needs_attention': return 'bg-amber-500/20 text-amber-600';
      case 'at_risk': return 'bg-orange-500/20 text-orange-600';
      case 'not_ready': return 'bg-red-500/20 text-red-600';
      default: return 'bg-gray-500/20 text-gray-600';
    }
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'improving': return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'declining': return <TrendingDown className="w-5 h-5 text-red-600" />;
      default: return <Minus className="w-5 h-5 text-slate-500" />;
    }
  };

  const exportComplianceReport = () => {
    if (!dashboardData) return;
    
    const report = {
      generatedAt: new Date().toISOString(),
      generatedBy: user?.name || user?.email,
      study: selectedStudy === 'all' ? 'All Studies' : studies.find(s => s.id === selectedStudy)?.name,
      overallScore: dashboardData.overall_score,
      overallStatus: dashboardData.overall_status,
      inspectionReadiness: dashboardData.inspection_readiness,
      totalDocumentsAssessed: dashboardData.total_documents_assessed,
      statusDistribution: dashboardData.status_distribution,
      riskDistribution: dashboardData.risk_distribution,
      regulationScores: dashboardData.regulation_scores,
      topGaps: dashboardData.top_gaps?.map(g => ({
        requirement: g.gap?.requirement_id,
        regulation: g.gap?.regulation,
        finding: g.gap?.finding,
        frequency: g.count
      })) || []
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-report-${format(new Date(), 'yyyy-MM-dd')}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Compliance report exported');
  };

  // Transform regulation scores for radar chart
  const getRadarData = () => {
    if (!dashboardData?.regulation_scores) return [];
    
    return Object.entries(dashboardData.regulation_scores).map(([key, data]) => ({
      regulation: data.name?.split(' - ')[0] || key,
      score: data.average_score || 0,
      fullMark: 100
    }));
  };

  // Transform status distribution for pie chart
  const getStatusPieData = () => {
    if (!dashboardData?.status_distribution) return [];
    
    return [
      { name: 'Compliant', value: dashboardData.status_distribution.compliant || 0, color: '#10B981' },
      { name: 'At Risk', value: dashboardData.status_distribution.at_risk || 0, color: '#F59E0B' },
      { name: 'Non-Compliant', value: dashboardData.status_distribution.non_compliant || 0, color: '#EF4444' }
    ].filter(d => d.value > 0);
  };

  // Transform regulation scores for bar chart
  const getRegulationBarData = () => {
    if (!dashboardData?.regulation_scores) return [];
    
    const standardInfo = {
      '21_cfr_part_11': { color: '#3B82F6', shortName: '21 CFR 11' },
      'ich_gcp_e6': { color: '#8B5CF6', shortName: 'ICH-GCP' },
      'eu_annex_11': { color: '#06B6D4', shortName: 'EU Annex 11' },
      'ema_gxp': { color: '#10B981', shortName: 'EMA GxP' },
      'mhra_gxp': { color: '#F59E0B', shortName: 'MHRA GxP' },
      'iso_13485': { color: '#EC4899', shortName: 'ISO 13485' },
      'alcoa_plus': { color: '#6366F1', shortName: 'ALCOA+' }
    };
    
    return Object.entries(dashboardData.regulation_scores).map(([key, data]) => ({
      name: standardInfo[key]?.shortName || key,
      score: data.average_score || 0,
      color: standardInfo[key]?.color || '#64748B'
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header - Dark Blue */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Compliance Center</h1>
                  <p className="text-xs text-white/60">Rule-Based Assessment Engine</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Study Display - Read-only when context study is set */}
              {contextStudy?.study_id ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50/10 border border-white/20" data-testid="study-locked">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-white truncate max-w-[180px]">{contextStudy.study_name}</span>
                    <span className="text-[10px] text-white/60">{contextStudy.protocol_number}</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] text-white/70 border-white/30">Locked</Badge>
                </div>
              ) : (
                <Select value={selectedStudy} onValueChange={setSelectedStudy}>
                  <SelectTrigger className="w-48 bg-slate-50/10 border-white/20 text-white" data-testid="study-select">
                    <SelectValue placeholder="Select Study" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white hover:bg-slate-700">All Studies</SelectItem>
                    {studies.map(study => (
                      <SelectItem key={study.id} value={study.id} className="text-white hover:bg-slate-700">
                        {study.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={runBulkAssessment}
                disabled={bulkAssessing}
                className="border-white/20 text-white hover:bg-slate-50/10"
                data-testid="bulk-assess-btn"
              >
                {bulkAssessing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Assessing...</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" />Run Assessment</>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={fetchComplianceData}
                className="border-white/20 text-white hover:bg-slate-50/10"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>

              <Button
                size="sm"
                onClick={exportComplianceReport}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-green-600 data-[state=active]:text-slate-900" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-green-600 data-[state=active]:text-slate-900" data-testid="tab-documents">
              Documents {(statusCounts.at_risk || 0) + (statusCounts.non_compliant || 0) > 0 && 
                <Badge className="ml-2 bg-amber-500 text-white">{(statusCounts.at_risk || 0) + (statusCounts.non_compliant || 0)}</Badge>
              }
            </TabsTrigger>
            <TabsTrigger value="regulations" className="data-[state=active]:bg-green-600 data-[state=active]:text-slate-900" data-testid="tab-regulations">Regulations</TabsTrigger>
            <TabsTrigger value="gaps" className="data-[state=active]:bg-green-600 data-[state=active]:text-slate-900" data-testid="tab-gaps">
              Gaps {(dashboardData?.top_gaps?.length || 0) > 0 && <Badge className="ml-2 bg-red-500 text-white">{dashboardData.top_gaps.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-green-600 data-[state=active]:text-slate-900" data-testid="tab-trends">Trends</TabsTrigger>
            {isAdmin() && <TabsTrigger value="audit" className="data-[state=active]:bg-green-600 data-[state=active]:text-slate-900" data-testid="tab-audit">Audit Trail</TabsTrigger>}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6" data-testid="overview-content">
            {/* Score Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Overall Score</p>
                      <p className={`text-4xl font-bold mt-1 ${getScoreColor(getOverallScore())}`} data-testid="overall-score">
                        {getOverallScore().toFixed(1)}%
                      </p>
                    </div>
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getScoreBg(getOverallScore())} flex items-center justify-center`}>
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-gradient-to-r ${getScoreBg(getOverallScore())} rounded-full transition-all duration-1000`}
                      style={{ width: `${getOverallScore()}%` }}
                    ></div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Documents Assessed</p>
                      <p className="text-4xl font-bold text-slate-900 mt-1" data-testid="docs-assessed">
                        {dashboardData?.total_documents_assessed || 0}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">Across {selectedStudy === 'all' ? studies.length : 1} study/studies</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Compliance Gaps</p>
                      <p className="text-4xl font-bold text-amber-600 mt-1" data-testid="gaps-count">
                        {dashboardData?.top_gaps?.length || 0}
                      </p>
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-7 h-7 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">
                    {(dashboardData?.risk_distribution?.high || 0) + (dashboardData?.risk_distribution?.critical || 0)} high/critical risk
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Inspection Ready</p>
                      <Badge className={`text-lg mt-1 ${getInspectionReadinessColor(dashboardData?.inspection_readiness)}`} data-testid="inspection-readiness">
                        {dashboardData?.inspection_readiness?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                      </Badge>
                    </div>
                    <div className={`w-14 h-14 rounded-xl ${dashboardData?.inspection_readiness === 'ready' ? 'bg-green-500/20' : 'bg-amber-500/20'} flex items-center justify-center`}>
                      {dashboardData?.inspection_readiness === 'ready' ? 
                        <CheckCircle className="w-7 h-7 text-green-600" /> : 
                        <AlertCircle className="w-7 h-7 text-amber-600" />
                      }
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-xs text-slate-500">Trend:</span>
                    {getTrendIcon(dashboardData?.trend_direction)}
                    <span className="text-xs text-slate-600 capitalize">{dashboardData?.trend_direction || 'stable'}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-2 gap-6">
              {/* Regulation Radar */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Regulation Compliance Radar
                  </CardTitle>
                  <CardDescription className="text-slate-600">Assessment across all 7 regulations</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={getRadarData()}>
                      <PolarGrid stroke="#CBD5E1" />
                      <PolarAngleAxis dataKey="regulation" stroke="#64748B" fontSize={10} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#64748B" fontSize={10} />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#10B981"
                        fill="#10B981"
                        fillOpacity={0.3}
                        strokeWidth={2}
                      />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Status Distribution */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Compliance Status Distribution
                  </CardTitle>
                  <CardDescription className="text-slate-600">Document compliance breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-8">
                    <ResponsiveContainer width="50%" height={200}>
                      <PieChart>
                        <Pie
                          data={getStatusPieData()}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          {getStatusPieData().map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-slate-700">Compliant</span>
                        </div>
                        <span className="font-bold text-slate-900">{dashboardData?.status_distribution?.compliant || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                          <span className="text-slate-700">At Risk</span>
                        </div>
                        <span className="font-bold text-slate-900">{dashboardData?.status_distribution?.at_risk || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-slate-700">Non-Compliant</span>
                        </div>
                        <span className="font-bold text-slate-900">{dashboardData?.status_distribution?.non_compliant || 0}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Regulation Scores Bar Chart */}
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Regulation-Specific Scores
                </CardTitle>
                <CardDescription className="text-slate-600">Average compliance score per regulation</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={getRegulationBarData()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} stroke="#64748B" fontSize={12} />
                    <YAxis dataKey="name" type="category" stroke="#64748B" fontSize={11} width={90} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" name="Score" radius={[0, 4, 4, 0]}>
                      {getRegulationBarData().map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab - Shows individual documents with compliance issues */}
          <TabsContent value="documents" className="space-y-4" data-testid="documents-content">
            {/* Status Filter Buttons */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-slate-600 text-sm mr-2">Filter by status:</span>
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('all')}
                className={statusFilter === 'all' ? 'bg-slate-700' : ''}
              >
                All
              </Button>
              <Button 
                variant={statusFilter === 'non_compliant' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('non_compliant')}
                className={statusFilter === 'non_compliant' ? 'bg-red-600' : 'border-red-300 text-red-600 hover:bg-red-50'}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Non-Compliant ({statusCounts.non_compliant || 0})
              </Button>
              <Button 
                variant={statusFilter === 'at_risk' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('at_risk')}
                className={statusFilter === 'at_risk' ? 'bg-amber-600' : 'border-amber-300 text-amber-600 hover:bg-amber-50'}
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                At Risk ({statusCounts.at_risk || 0})
              </Button>
              <Button 
                variant={statusFilter === 'compliant' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('compliant')}
                className={statusFilter === 'compliant' ? 'bg-green-600' : 'border-green-300 text-green-600 hover:bg-green-50'}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Compliant ({statusCounts.compliant || 0})
              </Button>
              <Button 
                variant={statusFilter === 'unassessed' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setStatusFilter('unassessed')}
                className={statusFilter === 'unassessed' ? 'bg-slate-500' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}
              >
                <Clock className="w-4 h-4 mr-1" />
                Unassessed ({statusCounts.unassessed || 0})
              </Button>
            </div>

            {documentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-2 text-slate-600">Loading documents...</span>
              </div>
            ) : documents.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Documents Found</h3>
                  <p className="text-slate-600">No documents match the selected filter criteria</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {documents.map((doc) => (
                  <Card 
                    key={doc.id} 
                    className={`bg-slate-50/90 border-slate-300 backdrop-blur-sm hover:border-slate-400 transition-colors cursor-pointer ${selectedDocument === doc.id ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => fetchDocumentDetails(doc.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            doc.compliance_status === 'non_compliant' ? 'bg-red-500/20' :
                            doc.compliance_status === 'at_risk' ? 'bg-amber-500/20' :
                            doc.compliance_status === 'compliant' ? 'bg-green-500/20' :
                            'bg-slate-200'
                          }`}>
                            {doc.compliance_status === 'non_compliant' ? <XCircle className="w-5 h-5 text-red-600" /> :
                             doc.compliance_status === 'at_risk' ? <AlertTriangle className="w-5 h-5 text-amber-600" /> :
                             doc.compliance_status === 'compliant' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                             <Clock className="w-5 h-5 text-slate-500" />}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-slate-900 font-medium">{doc.name}</h4>
                            <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                              <span>Type: {doc.document_type || 'Not set'}</span>
                              <span>Folder: {doc.folder_name || 'Unknown'}</span>
                              <span>Updated: {doc.updated_at ? format(parseISO(doc.updated_at), 'MMM dd, yyyy') : 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {doc.compliance_score !== undefined ? (
                            <div className="text-right">
                              <p className={`text-2xl font-bold ${getScoreColor(doc.compliance_score)}`}>
                                {doc.compliance_score?.toFixed(1)}%
                              </p>
                              <p className="text-xs text-slate-500">Score</p>
                            </div>
                          ) : (
                            <Badge className="bg-slate-200 text-slate-600">Not Assessed</Badge>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-slate-500 hover:text-green-600 hover:bg-green-50"
                            onClick={(e) => handleNavigateToActions(e, doc.id)}
                            title="Jump to Required Actions"
                            data-testid={`navigate-to-actions-${doc.id}`}
                          >
                            <ChevronRight className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Document Details Panel */}
            {selectedDocument && documentDetails && (
              <Card className="bg-white border-slate-300 shadow-lg mt-4">
                <CardHeader className="border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-slate-900">{documentDetails.document?.name}</CardTitle>
                      <CardDescription>
                        {documentDetails.folder_name} • Last assessed: {documentDetails.assessment?.assessed_at ? format(parseISO(documentDetails.assessment.assessed_at), 'MMM dd, yyyy HH:mm') : 'Never'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/dashboard?view=all&doc=${selectedDocument}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Document
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setSelectedDocument(null); setDocumentDetails(null); }}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {detailsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Score Summary */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-slate-100">
                          <p className="text-sm text-slate-600">Overall Score</p>
                          <p className={`text-2xl font-bold ${getScoreColor(documentDetails.assessment?.overall_score || 0)}`}>
                            {documentDetails.assessment?.overall_score?.toFixed(1) || 'N/A'}%
                          </p>
                        </div>
                        <div className="p-4 rounded-lg bg-slate-100">
                          <p className="text-sm text-slate-600">Status</p>
                          <Badge className={getSeverityColor(documentDetails.assessment?.overall_status)}>
                            {documentDetails.assessment?.overall_status?.replace('_', ' ') || 'Unknown'}
                          </Badge>
                        </div>
                        <div className="p-4 rounded-lg bg-slate-100">
                          <p className="text-sm text-slate-600">Risk Level</p>
                          <Badge className={getSeverityColor(documentDetails.assessment?.overall_risk)}>
                            {documentDetails.assessment?.overall_risk || 'Unknown'}
                          </Badge>
                        </div>
                        <div className="p-4 rounded-lg bg-slate-100">
                          <p className="text-sm text-slate-600">Issues to Fix</p>
                          <p className="text-2xl font-bold text-amber-600">{documentDetails.issue_count || 0}</p>
                        </div>
                      </div>

                      {/* Remediation Actions */}
                      {documentDetails.remediation_actions && documentDetails.remediation_actions.length > 0 && (
                        <div id="required-actions-section">
                          <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            Required Actions to Fix ({documentDetails.remediation_actions.length})
                          </h3>
                          <div className="space-y-3">
                            {documentDetails.remediation_actions.map((action, idx) => (
                              <div key={idx} className={`p-4 rounded-lg border ${action.priority === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                                <div className="flex items-start gap-3">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${action.priority === 'high' ? 'bg-red-500' : 'bg-amber-500'}`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-slate-900">{action.requirement_id}</span>
                                      <Badge className="text-xs">{action.regulation?.replace(/_/g, ' ')}</Badge>
                                      <Badge className={action.priority === 'high' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}>
                                        {action.priority}
                                      </Badge>
                                    </div>
                                    <p className="text-slate-700 text-sm mb-2"><strong>Issue:</strong> {action.issue}</p>
                                    <p className="text-green-700 text-sm"><strong>Action:</strong> {action.action_required}</p>
                                    <p className="text-slate-500 text-xs mt-1">Reference: {action.reference}</p>
                                    
                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                                      <TooltipProvider>
                                        <UITooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              size="sm"
                                              onClick={() => handleFixIssue(action)}
                                              className="bg-green-600 hover:bg-green-700 text-white"
                                              data-testid={`fix-issue-${idx}`}
                                            >
                                              <Wrench className="w-4 h-4 mr-1" />
                                              Fix Issue
                                              <Info className="w-3 h-3 ml-1 opacity-70" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="max-w-xs bg-slate-900 text-white border-slate-700 p-3">
                                            <p className="font-medium mb-1">How to fix this issue:</p>
                                            <p className="text-sm text-slate-300">
                                              {action.action || action.requirement || 'Navigate to the document and update the required information to resolve this compliance gap.'}
                                            </p>
                                          </TooltipContent>
                                        </UITooltip>
                                      </TooltipProvider>
                                      <TooltipProvider>
                                        <UITooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => handleOpenDismissDialog(action)}
                                              className="border-slate-300 text-slate-600 hover:bg-slate-100"
                                              data-testid={`not-required-${idx}`}
                                            >
                                              <Ban className="w-4 h-4 mr-1" />
                                              Not Required
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" className="max-w-xs bg-slate-900 text-white border-slate-700 p-3">
                                            <p className="text-sm text-slate-300">
                                              Mark this action as not required for this document. You will need to provide a reason.
                                            </p>
                                          </TooltipContent>
                                        </UITooltip>
                                      </TooltipProvider>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Dismissed Actions Section */}
                      {documentDetails.dismissed_actions && documentDetails.dismissed_actions.length > 0 && (
                        <div className="mt-6">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDismissedActions(!showDismissedActions)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            {showDismissedActions ? 'Hide' : 'Show'} Dismissed Actions ({documentDetails.dismissed_actions.length})
                          </Button>
                          
                          {showDismissedActions && (
                            <div className="space-y-2 mt-3">
                              {documentDetails.dismissed_actions.map((dismissed, idx) => (
                                <div key={idx} className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-slate-600">{dismissed.requirement_id}</span>
                                        <Badge className="text-xs bg-slate-200 text-slate-600">{dismissed.regulation}</Badge>
                                        <Badge className="text-xs bg-gray-400 text-white">
                                          {dismissed.is_not_applicable ? 'Not Applicable' : 'Dismissed'}
                                        </Badge>
                                      </div>
                                      <p className="text-xs text-slate-500 mt-1">
                                        Reason: {dismissed.reason}
                                      </p>
                                      <p className="text-xs text-slate-400">
                                        By {dismissed.dismissed_by_name} on {format(parseISO(dismissed.dismissed_at), 'MMM dd, yyyy')}
                                      </p>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleRestoreAction(dismissed)}
                                      className="text-blue-600 hover:bg-blue-50"
                                    >
                                      <RotateCcw className="w-4 h-4 mr-1" />
                                      Restore
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* No Issues */}
                      {(!documentDetails.remediation_actions || documentDetails.remediation_actions.length === 0) && (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                          <h4 className="text-lg font-semibold text-slate-900">No Issues Found</h4>
                          <p className="text-slate-600">This document meets all compliance requirements</p>
                          {documentDetails.dismissed_actions?.length > 0 && (
                            <p className="text-slate-400 text-sm mt-2">
                              ({documentDetails.dismissed_actions.length} actions marked as not applicable)
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Regulations Tab */}
          <TabsContent value="regulations" className="space-y-4" data-testid="regulations-content">
            <div className="grid grid-cols-2 gap-4">
              {REGULATORY_STANDARDS.map((std) => {
                const Icon = std.icon;
                const regData = dashboardData?.regulation_scores?.[std.key] || {};
                const score = regData.average_score || 0;
                return (
                  <Card key={std.key} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm hover:border-slate-400 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl ${score >= 80 ? 'bg-green-500/20' : score >= 60 ? 'bg-amber-500/20' : 'bg-red-500/20'} flex items-center justify-center`}>
                            <Icon className={`w-6 h-6 ${score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'}`} />
                          </div>
                          <div>
                            <h3 className="text-slate-900 font-semibold text-lg">{std.name}</h3>
                            <p className="text-slate-600 text-sm">{std.description}</p>
                            <Badge className="mt-1 bg-slate-200 text-slate-700">{std.region}</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-3xl font-bold ${getScoreColor(score)}`}>{score.toFixed(1)}%</p>
                          <p className="text-xs text-slate-500">Avg. Score</p>
                        </div>
                      </div>
                      <Progress value={score} className="h-2" />
                      {regData.min_score !== undefined && (
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                          <span>Min: {regData.min_score}%</span>
                          <span>Max: {regData.max_score}%</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Gaps Tab */}
          <TabsContent value="gaps" className="space-y-4" data-testid="gaps-content">
            {(!dashboardData?.top_gaps || dashboardData.top_gaps.length === 0) ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Major Compliance Gaps</h3>
                  <p className="text-slate-600">All documents meet compliance requirements or have not been assessed yet</p>
                </CardContent>
              </Card>
            ) : (
              dashboardData.top_gaps.map((gapItem, index) => {
                const gap = gapItem.gap || {};
                return (
                  <Card key={index} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${gap.risk_level === 'high' || gap.risk_level === 'critical' ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                          {gap.risk_level === 'high' || gap.risk_level === 'critical' ? 
                            <XCircle className="w-5 h-5 text-red-600" /> :
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          }
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-slate-900 font-semibold">{gap.requirement_id}: {gap.requirement_text?.slice(0, 60)}...</h3>
                            <Badge className={getSeverityColor(gap.risk_level)}>{gap.risk_level}</Badge>
                          </div>
                          <p className="text-slate-600 text-sm mb-2">{gap.finding}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
                            <span>Regulation: {gap.regulation?.replace(/_/g, ' ')}</span>
                            <span>Reference: {gap.regulatory_reference}</span>
                            <span>Occurrences: <strong>{gapItem.count}</strong></span>
                          </div>
                          <div className="bg-slate-100 rounded-lg p-3">
                            <p className="text-sm text-slate-700"><strong>Recommendation:</strong> {gap.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6" data-testid="trends-content">
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-600 text-sm">Trend Direction</p>
                      <div className="flex items-center gap-2 mt-2">
                        {getTrendIcon(dashboardData?.trend_direction)}
                        <span className="text-xl font-bold text-slate-900 capitalize">
                          {dashboardData?.trend_direction || 'Stable'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm col-span-2">
                <CardContent className="p-6">
                  <h3 className="text-slate-900 font-semibold mb-4">Risk Distribution</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {['low', 'medium', 'high', 'critical'].map(level => (
                      <div key={level} className={`p-3 rounded-lg ${getSeverityColor(level)}`}>
                        <p className="text-2xl font-bold">{dashboardData?.risk_distribution?.[level] || 0}</p>
                        <p className="text-xs capitalize">{level} Risk</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Score Trend Chart */}
            {dashboardData?.score_trend && dashboardData.score_trend.length > 0 && (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900">Compliance Score Trend</CardTitle>
                  <CardDescription>Weekly average compliance scores</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={dashboardData.score_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="week" stroke="#64748B" fontSize={11} />
                      <YAxis domain={[0, 100]} stroke="#64748B" fontSize={11} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="average_score" name="Score" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Predicted Issues */}
            {dashboardData?.predicted_issues && dashboardData.predicted_issues.length > 0 && (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    Predicted Issues
                  </CardTitle>
                  <CardDescription>Potential compliance risks based on trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData.predicted_issues.map((issue, index) => (
                      <div key={index} className={`p-4 rounded-lg border ${getSeverityColor(issue.severity)}`}>
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">{issue.type?.replace(/_/g, ' ')}</span>
                        </div>
                        <p className="text-sm mt-1">{issue.message}</p>
                        {issue.regulation && <Badge className="mt-2">{issue.regulation}</Badge>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Audit Trail Tab */}
          {isAdmin() && (
            <TabsContent value="audit" data-testid="audit-content">
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <History className="w-5 h-5 text-cyan-600" />
                    Audit Trail
                  </CardTitle>
                  <CardDescription className="text-slate-600">Complete system activity log</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-96">
                    <div className="space-y-2">
                      {auditLogs.slice(0, 50).map((log, index) => (
                        <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-slate-100/80 hover:bg-slate-100 transition-colors">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <Activity className="w-4 h-4 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-slate-900 text-sm">
                              <span className="font-medium">{log.user_email || 'System'}</span>
                              <span className="text-slate-600"> {log.action?.replace(/_/g, ' ')} </span>
                              <span className="text-slate-700">{log.entity_type}</span>
                            </p>
                            <p className="text-xs text-slate-500">
                              {log.created_at ? format(parseISO(log.created_at), 'MMM dd, yyyy HH:mm:ss') : 'Unknown'}
                            </p>
                          </div>
                          <Badge className="bg-slate-200 text-slate-700 text-xs">{log.entity_type}</Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Dismiss Action Dialog */}
      <Dialog open={dismissDialogOpen} onOpenChange={setDismissDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Mark as Not Required</DialogTitle>
            <DialogDescription className="text-slate-600">
              Provide a reason for marking this compliance action as not required or not applicable.
            </DialogDescription>
          </DialogHeader>
          {actionToDismiss && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-slate-100">
                <p className="text-sm font-medium text-slate-900">{actionToDismiss.requirement_id}</p>
                <p className="text-xs text-slate-600 mt-1">{actionToDismiss.issue}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dismiss-reason" className="text-slate-700">Reason for dismissal *</Label>
                <Textarea
                  id="dismiss-reason"
                  placeholder="e.g., Not applicable to this document type, Already addressed in another way..."
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  className="bg-white border-slate-300 text-slate-900"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDismissDialogOpen(false)}
              className="border-slate-300 text-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDismissAction(true)}
              disabled={dismissingAction || !dismissReason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {dismissingAction ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}
              Mark as Not Required
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
